import React, { useEffect, useId, useLayoutEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, X } from 'lucide-react';
import Button from '../Button';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './SidePanel.css';
import { drawerDurationMs } from '../utils/cssDuration';

import { MOBILE_QUERY } from '../utils/breakpoints';

/**
 * SidePanel — Generic DS right-side drawer
 *
 * Desktop: 880px panel sliding from right
 * Mobile: full-width panel — or, with `mobileSheet`, a bottom sheet with a
 *         swipe handle (the title is kept as the dialog's accessible name
 *         only; the X sits in the top-right corner)
 *
 * Renders `children` inside a scrollable body area.
 *
 * Props:
 *  - isOpen      — controlled open state
 *  - onClose     — close callback, with how it was closed: 'close' (the X),
 *                  'overlay' (outside click), 'escape' or 'swipe' (the sheet)
 *  - title       — header title (string or ReactNode)
 *  - size        — 'l' (880px, default) | 'm' (616px) desktop width; a brand
 *                  restates the default through --side-panel-width
 *  - floating    — desktop: inset from every screen edge, rounded all round
 *                  (the SideMenu geometry) instead of flush with the right
 *                  edge; below 1024px the panel is flush as usual
 *  - mobileSheet — below 1024px, open as a bottom sheet instead of a full-width panel
 *  - sheetHeader — on the sheet: 'floating' (default — only the X, over the
 *                  content) | 'row' (back · title · X stay a real row)
 *  - overlay     — 'dim' (default) darkens the page; 'clear' keeps it visible
 *                  (still closes on an outside click)
 *  - actions     — a row of buttons after the content: in flow on desktop
 *                  (with `floating`: a frosted bar pinned to the panel's
 *                  bottom), a sticky full-bleed bar at the sheet's bottom on
 *                  mobile
 *  - pinActions  — false keeps `actions` in flow right under the content on a
 *                  `floating` desktop panel too (no pinned bar); the mobile
 *                  sheet keeps its sticky bar either way
 *  - stepKey     — step flows: a change re-keys the title and the body (a
 *                  short enter motion) and moves focus to the title
 *  - focusOnStep — false when the new step focuses its own field
 *  - restoreFocus— give focus back to whatever opened the panel once it closed
 *  - bodyRef     — ref to the scrolling body (scroll a field into view)
 *  - closeLabel  — accessible name of the X button
 *  - onBack      — renders a back icon button left of the title (step flows)
 *  - backLabel   — accessible name of the back button
 *  - className   — extra class on the panel (placement; the skin comes from
 *                  the --side-panel-* tokens)
 *  - children    — any content to render inside the panel body
 */
const SidePanel = ({
  isOpen = false,
  onClose,
  title = '',
  size = 'l',
  floating = false,
  mobileSheet = false,
  sheetHeader = 'floating',
  overlay = 'dim',
  actions,
  pinActions = true,
  stepKey,
  focusOnStep = true,
  restoreFocus = false,
  bodyRef: bodyRefProp,
  closeLabel = 'Close',
  onBack,
  backLabel = 'Back',
  className = '',
  children,
}) => {
  const titleId = useId();
  const titleRef = useRef(null);
  const openerRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);
  // Still on screen: stays true through the slide-out after `isOpen` drops
  const [isShown, setIsShown] = useState(isOpen);
  const closeTimer = useRef(null);
  const bodyRef = useRef(null);
  // The consumer's bodyRef (object or callback) sees the same element
  const setBodyRef = useCallback((el) => {
    bodyRef.current = el;
    if (typeof bodyRefProp === 'function') bodyRefProp(el);
    else if (bodyRefProp) bodyRefProp.current = el;
  }, [bodyRefProp]);

  const slideOut = useCallback((then) => {
    setIsClosing(true);
    setIsEntered(false);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      setIsShown(false);
      then?.();
      // Closed: hand focus back to the opener (restoreFocus)
      const opener = openerRef.current;
      openerRef.current = null;
      if (restoreFocus && opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    }, drawerDurationMs());
  }, [restoreFocus]);

  /* Remember what opened the panel (for restoreFocus). Layout effect, declared
     BEFORE the step-focus effect below: a step flow moves focus onto the title
     in the same commit, and a passive effect would record the title instead
     of the opener. */
  useLayoutEffect(() => {
    if (isOpen) openerRef.current = document.activeElement;
  }, [isOpen]);

  /* Step flows: a new step puts focus on its title — the screen reader
     announces where the user is; the title is tabIndex -1, never in the tab
     order. Layout effect: before paint, so nothing flashes unfocused. */
  const shownStep = useRef(null);
  useLayoutEffect(() => {
    if (stepKey === undefined || !isOpen) {
      shownStep.current = null;
      return;
    }
    if (shownStep.current === stepKey) return;
    shownStep.current = stepKey;
    if (focusOnStep) titleRef.current?.focus({ preventScroll: true });
  }, [stepKey, isOpen, focusOnStep]);

  /* X / overlay / Escape / swipe: slide out, then report — with the reason,
     so a consumer can tell "dismissed" (outside, swipe) from "cancelled" */
  const handleClose = useCallback(
    (reason) => slideOut(() => onClose?.(reason)),
    [slideOut, onClose],
  );

  /* The parent closing it (`isOpen` → false) slides out too */
  useEffect(() => {
    if (isOpen) {
      clearTimeout(closeTimer.current);
      setIsClosing(false);
      setIsShown(true);
    } else if (isShown && !isClosing) {
      slideOut();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* Swipe-to-close — sheet only, and only while it IS a sheet (< 1024px) */
  const swipeClose = useCallback(() => {
    if (window.matchMedia(MOBILE_QUERY).matches) handleClose('swipe');
  }, [handleClose]);
  const {
    containerRef: sheetRef,
    dragOffset,
    isDragging,
    DragHandle,
  } = useSwipeToClose(swipeClose, isOpen && mobileSheet);

  /* Body scroll lock */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  /* Once the slide-in has played, drop it — a filled keyframe would
     override the sheet's drag transform */
  useEffect(() => {
    if (!isOpen || !mobileSheet) return undefined;
    const timer = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
    return () => {
      clearTimeout(timer);
      setIsEntered(false);
    };
  }, [isOpen, mobileSheet]);

  /* Track the visual viewport so the panel can sit exactly on the visible area.
   * iOS Safari positions `position: fixed` against the LAYOUT viewport, which
   * does not shrink for the keyboard — see SidePanel.css for what breaks. */
  useEffect(() => {
    if (!isOpen && !isClosing) return undefined;

    const viewport = window.visualViewport;
    const root = document.documentElement;

    const apply = () => {
      const height = Math.round(viewport ? viewport.height : window.innerHeight);
      const offset = Math.round(viewport ? viewport.offsetTop : 0);
      root.style.setProperty('--side-panel-vp-height', `${height}px`);
      root.style.setProperty('--side-panel-vp-offset', `${offset}px`);
    };

    apply();
    // `scroll` matters too: iOS shifts the visual viewport as the keyboard opens.
    viewport?.addEventListener('resize', apply);
    viewport?.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);

    return () => {
      viewport?.removeEventListener('resize', apply);
      viewport?.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      root.style.removeProperty('--side-panel-vp-height');
      root.style.removeProperty('--side-panel-vp-offset');
    };
  }, [isOpen, isClosing]);

  /* Keep a focused field clear of the keyboard.
   * The body is the scroller and the panel is pinned to the visual viewport,
   * so the body's bottom edge IS the top of the keyboard — a field tapped near
   * the bottom of a long form would otherwise sit behind it. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const body = bodyRef.current;
    if (!body) return undefined;

    const GAP = 16;
    const isField = (el) =>
      !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

    let timer = null;

    const reveal = () => {
      const field = document.activeElement;
      if (!isField(field) || !body.contains(field)) return;

      // Move the whole field (floating label included), not the bare input.
      const target = field.closest('.input-wrapper') || field;
      const fieldRect = target.getBoundingClientRect();
      const bodyRect = body.getBoundingClientRect();

      // Prefer scrolling all the way down — that also brings whatever follows
      // the field into view (a trailing "add" / submit button), which a minimal
      // reveal leaves just below the fold. Only when the field itself stays
      // visible afterwards, so focusing field 1 of a long form still reveals
      // minimally instead of jumping to the end.
      const toBottom = body.scrollHeight - body.clientHeight - body.scrollTop;
      if (toBottom > 1 && fieldRect.top - toBottom >= bodyRect.top + GAP) {
        body.scrollBy({ top: toBottom, behavior: 'smooth' });
        return;
      }

      let delta = 0;
      if (fieldRect.bottom > bodyRect.bottom - GAP) {
        delta = fieldRect.bottom - (bodyRect.bottom - GAP);
      } else if (fieldRect.top < bodyRect.top + GAP) {
        delta = fieldRect.top - (bodyRect.top + GAP);
      }
      if (Math.abs(delta) > 1) body.scrollBy({ top: delta, behavior: 'smooth' });
    };

    const schedule = (delay) => {
      clearTimeout(timer);
      timer = setTimeout(reveal, delay);
    };

    // The keyboard animates in and shrinks the panel, which moves the body's
    // bottom — settle on the last viewport change instead of a fixed delay.
    const onFocusIn = (e) => {
      if (isField(e.target)) schedule(300);
    };
    const onViewportResize = () => {
      if (isField(document.activeElement)) schedule(150);
    };

    body.addEventListener('focusin', onFocusIn);
    window.visualViewport?.addEventListener('resize', onViewportResize);

    return () => {
      clearTimeout(timer);
      body.removeEventListener('focusin', onFocusIn);
      window.visualViewport?.removeEventListener('resize', onViewportResize);
    };
  }, [isOpen]);

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') handleClose('escape');
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, handleClose]);

  if (!isOpen && !isShown) return null;

  const panelClasses = [
    'side-panel',
    size === 'm' && 'side-panel--m',
    floating && 'side-panel--floating',
    floating && actions && !pinActions && 'side-panel--actions-inline',
    mobileSheet && 'side-panel--sheet',
    mobileSheet && sheetHeader === 'row' && 'side-panel--sheet-header-row',
    isEntered && 'side-panel--entered',
    isDragging && 'side-panel--dragging',
    isClosing && 'side-panel--closing',
    className,
  ].filter(Boolean).join(' ');

  const isStepped = stepKey !== undefined;
  // Step flows re-key the body so the enter motion replays per step
  const stepContent = isStepped
    ? <div key={stepKey} className="side-panel__step">{children}</div>
    : children;

  // Read only by the mobile sheet rule, so a drag never moves the desktop panel
  const sheetStyle = mobileSheet ? { '--side-panel-drag': `${dragOffset}px` } : undefined;

  return (
    <>
      {/* Overlay */}
      <div
        className={`side-panel__overlay side-panel__overlay--${overlay}${mobileSheet ? ' side-panel__overlay--sheet' : ''}${isClosing ? ' side-panel__overlay--closing' : ''}`}
        onClick={() => handleClose('overlay')}
      />

      {/* Panel */}
      <div
        ref={mobileSheet ? sheetRef : undefined}
        className={panelClasses}
        style={sheetStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {mobileSheet && <DragHandle className="side-panel__swipe-handle" />}

        {/* Sticky Header */}
        <div className="side-panel__header">
          {onBack && (
            <Button
              size="tertiary"
              variant="gray"
              iconOnly
              iconLeft={<ArrowLeft size={20} />}
              onClick={onBack}
              aria-label={backLabel}
              className="side-panel__back"
            />
          )}
          <h3
            id={titleId}
            key={isStepped ? stepKey : undefined}
            ref={titleRef}
            tabIndex={isStepped ? -1 : undefined}
            className={`side-panel__title typo-h3${isStepped ? ' side-panel__title--step' : ''}`}
          >
            {title}
          </h3>
          <Button
            size="tertiary"
            variant="gray"
            iconOnly
            iconLeft={<X size={20} />}
            onClick={() => handleClose('close')}
            aria-label={closeLabel}
            className="side-panel__close"
          />
        </div>

        {/* Scrollable content (overflow-y-auto: the swipe hook's scroll probe) */}
        <div className={`side-panel__body overflow-y-auto${actions ? ' side-panel__body--with-actions' : ''}`} ref={setBodyRef}>
          {actions ? (
            <>
              <div className="side-panel__content">{stepContent}</div>
              <div className="side-panel__actions">{actions}</div>
            </>
          ) : stepContent}
        </div>
      </div>
    </>
  );
};

SidePanel.propTypes = {
  /** Controlled open state */
  isOpen: PropTypes.bool,
  /** Close callback — fires after the close animation with the reason:
      'close' (X) | 'overlay' | 'escape' | 'swipe' */
  onClose: PropTypes.func,
  /** Header title; on the mobile sheet it is the dialog's accessible name only */
  title: PropTypes.node,
  /** Desktop width: 'l' 880px (default) or 'm' 616px — a brand restates the default via --side-panel-width */
  size: PropTypes.oneOf(['l', 'm']),
  /** Desktop: inset from every screen edge and rounded all round (the SideMenu geometry) instead of flush right; flush below 1024px */
  floating: PropTypes.bool,
  /** Below 1024px: open as a bottom sheet with a swipe handle instead of a full-width panel */
  mobileSheet: PropTypes.bool,
  /** On the sheet: 'floating' = only the X over the content (default), 'row' = back · title · X stay a real row */
  sheetHeader: PropTypes.oneOf(['floating', 'row']),
  /** A row of buttons after the content — in flow on desktop, a sticky full-bleed bar at the sheet's bottom on mobile */
  actions: PropTypes.node,
  /** false: `actions` stay in flow under the content on the floating desktop panel */
  pinActions: PropTypes.bool,
  /** Step flows: a change re-keys the title and body (enter motion) and moves focus to the title */
  stepKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** false when the new step focuses its own field instead of the title */
  focusOnStep: PropTypes.bool,
  /** Give focus back to whatever opened the panel once it has closed */
  restoreFocus: PropTypes.bool,
  /** Ref to the scrolling body (object or callback) — scroll a field into view from a consumer */
  bodyRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  /** 'dim' darkens the page behind; 'clear' keeps it visible (an outside click still closes) */
  overlay: PropTypes.oneOf(['dim', 'clear']),
  /** Accessible name of the X button */
  closeLabel: PropTypes.string,
  /** Renders a back icon button left of the title — for drawers with steps */
  onBack: PropTypes.func,
  /** Accessible name of the back button */
  backLabel: PropTypes.string,
  /** Extra class on the panel — placement hooks; the skin is the --side-panel-* tokens, the width `size` */
  className: PropTypes.string,
  /** Panel body content */
  children: PropTypes.node,
};

export default SidePanel;
